import React, { useState, useEffect } from 'react';
import { isRebateVendor } from '../../../../../../../utils/vendorCategoryUtils';

// API: 업체 데이터 타입
interface VendorItem {
  vendorIdx: number;
  name: string;
  contact: string;
  category: {
    vendorCategoryIdx: number;
    name: string;
    parent?: {
      vendorCategoryIdx: number;
      name: string;
    } | null;
  };
}

// 카테고리 트리 (API에서 조회)
interface CategoryTreeItem {
  vendorCategoryIdx: number;
  name?: string;
  label?: string;
  orderNo?: number;
  children?: CategoryTreeItem[];
}

// API: 상품 데이터 타입
interface SearchedProduct {
  productIdx: number;
  name: string;
  categoryName: string;
  salesPrice: number;
  costPrice: number | null;
  vatAmount: number;
}

// 부모에게 전달할 데이터 타입
export interface NewProductData {
  productIdx?: number;
  vendorIdx?: number;
  vendorCategoryIdx?: number;
  partnerVendorIdx?: number;
  vendorName?: string;
  productName: string;
  categoryName: string;
  quantity: number;
  supplyPrice: number;
  salesPrice: number;
  vat: number;
  memo: string;
  // 혼수품목 전용
  contractDate?: string;
  contractItems?: string;
  deposit?: number;
  rebateRate?: number;
  rebateAmount?: number;
  expectedPaymentDate?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: NewProductData) => Promise<void>;
  initialData?: NewProductData | null;
}

const CUSTOMER_PRODUCT_CATEGORIES = ['본식', '리허설', '본식 + 리허설'] as const;

export default function AddProductModal({ isOpen, onClose, onSave, initialData }: Props) {
  // --- State ---
  const [step, setStep] = useState<1 | 2>(1);
  const isEditMode = !!initialData;

  // Step 1: 업체 관련
  const [vendors, setVendors] = useState<VendorItem[]>([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [categoryTree, setCategoryTree] = useState<CategoryTreeItem[]>([]);
  const [selectedParentIdx, setSelectedParentIdx] = useState<number | ''>('');
  const [selectedSubIdx, setSelectedSubIdx] = useState<number | ''>('');
  const [selectedVendor, setSelectedVendor] = useState<VendorItem | null>(null);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);

  // Step 2: 상품 관련
  const [products, setProducts] = useState<SearchedProduct[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // 폼 데이터
  const [formData, setFormData] = useState<NewProductData>({
    partnerVendorIdx: 0,
    productName: '',
    vendorIdx: 0,
    vendorCategoryIdx: 0,
    categoryName: '',
    quantity: 1,
    supplyPrice: 0,
    salesPrice: 0,
    vat: 0,
    memo: ''
  });

  // --- Derived ---
  const selectedParent = categoryTree.find((p) => p.vendorCategoryIdx === selectedParentIdx);
  const subCategoryOptions = selectedParent?.children || [];

  const filteredVendors = vendors.filter((v) => {
    const nameMatch = v.name.toLowerCase().includes(vendorSearch.toLowerCase());
    if (!nameMatch) return false;

    if (selectedSubIdx) {
      return v.category?.vendorCategoryIdx === selectedSubIdx;
    }
    if (selectedParentIdx) {
      const childIdxs = selectedParent?.children?.map((c) => c.vendorCategoryIdx) || [];
      return (
        v.category?.vendorCategoryIdx === selectedParentIdx ||
        childIdxs.includes(v.category?.vendorCategoryIdx)
      );
    }
    return true;
  });

  // --- Effects ---
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setStep(2);
        setFormData(initialData);
      } else {
        setStep(1);
        setVendorSearch('');
        setSelectedParentIdx('');
        setSelectedSubIdx('');
        setProductSearch('');
        setSelectedVendor(null);
        setProducts([]);
        setFormData({
          partnerVendorIdx: 0,
          productName: '',
          categoryName: '',
          quantity: 1,
          supplyPrice: 0,
          salesPrice: 0,
          vat: 0,
          memo: ''
        });
        fetchCategories();
        fetchVendors();
      }
    }
  }, [isOpen, initialData]);

  // --- API Calls ---
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/category', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const tree: CategoryTreeItem[] = Array.isArray(result)
        ? result
        : result?.status ? result.data : [];
      const sorted = [...tree].sort(
        (a, b) => (a.orderNo ?? 999) - (b.orderNo ?? 999)
      );
      sorted.forEach((p) => {
        if (p.children) {
          p.children = [...p.children].sort((a, b) => (a.orderNo ?? 999) - (b.orderNo ?? 999));
        }
      });
      setCategoryTree(sorted);
    } catch (error) {
      console.error('Fetch Categories Error:', error);
      setCategoryTree([]);
    }
  };

  const fetchVendors = async () => {
    setIsLoadingVendors(true);
    try {
      const token = localStorage.getItem('accessToken');

      // 내 업체 정보 + 업체 목록 동시 호출
      const [meRes, vendorRes] = await Promise.all([
        fetch('https://api.wedmin.biz/vendor/me', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('https://api.wedmin.biz/vendor?status=ACTIVE&size=100', { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);

      const meResult = await meRes.json();
      const vendorResult = await vendorRes.json();

      const vendorList: VendorItem[] =
        vendorResult.status && vendorResult.data && Array.isArray(vendorResult.data.vendors)
          ? vendorResult.data.vendors
          : [];

      // 내 업체를 최상단에 추가
      if (meResult.status && meResult.data) {
        const me = meResult.data;
        const myVendor: VendorItem = {
          vendorIdx: me.vendorIdx,
          name: `${me.name} (내 업체)`,
          contact: me.contact || '',
          category: me.category || { vendorCategoryIdx: 0, name: '웨딩플래너' },
        };
        // 중복 제거 후 최상단에 추가
        const filtered = vendorList.filter((v) => v.vendorIdx !== me.vendorIdx);
        setVendors([myVendor, ...filtered]);
      } else {
        setVendors(vendorList);
      }
    } catch (error) {
      console.error('Fetch Vendors Error:', error);
      setVendors([]);
    } finally {
      setIsLoadingVendors(false);
    }
  };

  const searchProducts = async (query: string, targetVendorIdx?: number) => {
    const currentVendorIdx = targetVendorIdx || selectedVendor?.vendorIdx;
    if (!currentVendorIdx) return;

    setIsLoadingProducts(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = `https://api.wedmin.biz/product?partnerVendorIdx=${currentVendorIdx}${query ? `&q=${query}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const items = result.items || (result.data && result.data.items);
      setProducts(items || []);
    } catch (error) {
      console.error('Search Products Error:', error);
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // --- Handlers ---
  const handleSelectVendor = (vendor: VendorItem) => {
    setSelectedVendor(vendor);
    setFormData((prev) => ({ ...prev, partnerVendorIdx: vendor.vendorIdx }));
    setStep(2);
    setProducts([]);
    setProductSearch('');
    searchProducts('', vendor.vendorIdx);
  };

  // 리베이트 기반 업체 판별 (혼수업체, 웨딩홀 등)
  const isRebate = isRebateVendor(selectedVendor);

  const handleDirectInput = () => {
    setSelectedVendor(null);
    setFormData((prev) => ({
      ...prev,
      partnerVendorIdx: 0,
      vendorIdx: 0,
      vendorCategoryIdx: 0,
    }));
    setStep(2);
    setProducts([]);
    setProductSearch('');
  };

  const handleProductSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchProducts(productSearch, selectedVendor?.vendorIdx);
    }
  };

  const handleSelectProduct = (prod: SearchedProduct) => {
    if (isRebate) {
      // 리베이트 업체: 계약품목에 상품명 세팅
      setFormData((prev) => ({
        ...prev,
        productIdx: prod.productIdx,
        contractItems: prod.name,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        productIdx: prod.productIdx,
        productName: prod.name,
        supplyPrice: typeof prod.costPrice === 'number' ? prod.costPrice : 0,
        salesPrice: prod.salesPrice,
        vat: prod.vatAmount,
      }));
    }
    setProducts([]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'quantity' || name.includes('Price') || name === 'vat' ? Number(value) : value
    }));
  };

  const handleSubmit = async () => {
    if (isRebate) {
      // 혼수품목: contractItems를 productName으로 사용
      if (!formData.contractItems) {
        alert('계약품목을 입력해주세요.');
        return;
      }
      formData.productName = formData.contractItems;
    } else {
      if (!formData.productName) {
        alert('상품명을 입력해주세요.');
        return;
      }
      if (!CUSTOMER_PRODUCT_CATEGORIES.includes(formData.categoryName as (typeof CUSTOMER_PRODUCT_CATEGORIES)[number])) {
        alert('카테고리를 선택해주세요. (본식 / 리허설 / 본식 + 리허설)');
        return;
      }
    }
    formData.vendorIdx = selectedVendor?.vendorIdx || 0;
    formData.vendorCategoryIdx = selectedVendor?.category?.vendorCategoryIdx || 0;
    await onSave(formData);
    onClose();
  };

  const handleGoBack = () => {
    setStep(1);
    setProducts([]);
    setSelectedVendor(null);
  };

  if (!isOpen) return null;
  const step2TitleVendorName = selectedVendor?.name || initialData?.vendorName;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button onClick={handleGoBack} className="text-gray-500 hover:text-gray-800">
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {step === 1 ? '업체 선택' : isEditMode ? '계약 수정' : `${step2TitleVendorName || '직접 입력'} 계약 등록`}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">

          {/* === STEP 1: 업체 선택 === */}
          {step === 1 && (
            <div className="space-y-4">
              {/* 카테고리 필터: 업체 데이터에서 추출한 대분류/소분류 */}
              <div className="flex gap-2">
                <select
                  value={selectedParentIdx}
                  onChange={(e) => {
                    setSelectedParentIdx(e.target.value ? Number(e.target.value) : '');
                    setSelectedSubIdx('');
                  }}
                  className="w-1/3 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none bg-white text-sm"
                >
                  <option value="">대분류 전체</option>
                  {categoryTree.map((cat) => (
                    <option key={cat.vendorCategoryIdx} value={cat.vendorCategoryIdx}>{cat.label || cat.name}</option>
                  ))}
                </select>

                <select
                  value={selectedSubIdx}
                  onChange={(e) => setSelectedSubIdx(e.target.value ? Number(e.target.value) : '')}
                  disabled={!selectedParentIdx || subCategoryOptions.length === 0}
                  className="w-1/3 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none bg-white text-sm"
                >
                  <option value="">소분류 전체</option>
                  {subCategoryOptions.map((cat) => (
                    <option key={cat.vendorCategoryIdx} value={cat.vendorCategoryIdx}>{cat.label || cat.name}</option>
                  ))}
                </select>

                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="업체명 검색..."
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              {/* 직접 입력 버튼 */}
              <button
                onClick={handleDirectInput}
                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-edit-line"></i>
                업체 선택 없이 직접 입력
              </button>

              {/* 업체 목록 */}
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto divide-y divide-gray-100">
                {isLoadingVendors ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  </div>
                ) : filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor) => (
                    <div
                      key={vendor.vendorIdx}
                      onClick={() => handleSelectVendor(vendor)}
                      className="p-4 hover:bg-teal-50 cursor-pointer transition-colors flex justify-between items-center group"
                    >
                      <div>
                        <div className="font-medium text-gray-900 group-hover:text-teal-700">{vendor.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {vendor.category?.parent?.name && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-1">
                              {vendor.category.parent.name}
                            </span>
                          )}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                            {vendor.category?.name || '미지정'}
                          </span>
                          {vendor.contact}
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-teal-600 text-lg"></i>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    검색된 업체가 없습니다.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === STEP 2: 상품 입력 === */}
          {step === 2 && (
            <div className="space-y-6">
              {selectedVendor && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{isRebate ? '등록된 계약품목 불러오기 (선택)' : '등록된 상품 불러오기 (선택)'}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      onKeyDown={handleProductSearchKeyDown}
                      placeholder="상품명을 입력하고 엔터..."
                      className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <button
                      onClick={() => searchProducts(productSearch, selectedVendor?.vendorIdx)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600"
                    >
                      <i className="ri-search-line text-lg"></i>
                    </button>
                  </div>

                  {isLoadingProducts ? (
                    <div className="p-4 text-center text-sm text-gray-500">검색 중...</div>
                  ) : products.length > 0 ? (
                    <ul className="mt-2 bg-white border border-gray-200 rounded-md max-h-40 overflow-y-auto shadow-sm divide-y divide-gray-50">
                      {products.map((prod) => (
                        <li
                          key={prod.productIdx}
                          onClick={() => handleSelectProduct(prod)}
                          className="px-4 py-2 hover:bg-teal-50 cursor-pointer flex justify-between items-center text-sm"
                        >
                          <div>
                            <span className="text-gray-900 font-medium block">{prod.name}</span>
                            <span className="text-xs text-gray-500">{prod.categoryName}</span>
                          </div>
                          {isRebate
                            ? <span className="text-purple-600 text-xs">{prod.name}</span>
                            : <span className="text-teal-600 font-medium">{prod.salesPrice.toLocaleString()}원</span>
                          }
                        </li>
                      ))}
                    </ul>
                  ) : (
                    !isLoadingProducts && (
                      <div className="mt-2 text-xs text-center py-3">
                        {productSearch ? (
                          <span className="text-gray-500">검색 결과가 없습니다.</span>
                        ) : (
                          <div className="text-amber-600">
                            <i className="ri-information-line mr-1"></i>
                            등록된 상품이 없습니다. 업체관리에서 해당 업체의 상품을 먼저 등록해주세요.
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}

              {!selectedVendor && !isEditMode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center gap-2">
                  <i className="ri-information-line"></i>
                  업체 선택 없이 직접 입력 모드입니다.
                </div>
              )}

              <hr className="border-gray-100" />

              {/* ===== 혼수업체: 전용 폼 ===== */}
              {isRebate ? (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-purple-700 flex items-center gap-1 border-b border-gray-200 pb-2">
                    <i className="ri-gift-line"></i> 리베이트 계약 정보
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">계약일자</label>
                      <input type="date" name="contractDate" value={formData.contractDate || ''} onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">입금예정일</label>
                      <input type="date" name="expectedPaymentDate" value={formData.expectedPaymentDate || ''} onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">계약품목</label>
                    <input type="text" name="contractItems" value={formData.contractItems || ''} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="예: 양복 2벌, 셔츠 3장" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">계약금</label>
                      <input type="number" name="deposit" value={formData.deposit || ''} onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">리베이트 (%)</label>
                      <input type="number" name="rebateRate" value={formData.rebateRate || ''} step="0.1"
                        onChange={(e) => {
                          const rate = Number(e.target.value);
                          setFormData((prev) => ({ ...prev, rebateRate: rate, rebateAmount: Math.round((prev.deposit || 0) * rate / 100) }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="%" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">리베이트 금액</label>
                      <input type="number" name="rebateAmount" value={formData.rebateAmount || ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, rebateAmount: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="0" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">메모</label>
                    <textarea name="memo" value={formData.memo} onChange={handleChange} rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                </div>
              ) : (
                /* ===== 일반 상품 폼 ===== */
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">상품명 <span className="text-red-500">*</span></label>
                    <input type="text" name="productName" value={formData.productName} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="상품명을 직접 입력하세요" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">진행 구분</label>
                    <select name="categoryName" value={formData.categoryName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, categoryName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                      <option value="">진행 구분 선택</option>
                      {CUSTOMER_PRODUCT_CATEGORIES.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">수량</label>
                    <input type="number" name="quantity" value={formData.quantity} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">판매가 (고객청구액)</label>
                    <input type="number" name="salesPrice" value={formData.salesPrice} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">공급가 (원가)</label>
                    <input type="number" name="supplyPrice" value={formData.supplyPrice} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">메모</label>
                    <textarea name="memo" value={formData.memo} onChange={handleChange} rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">취소</button>
          {step === 2 && (
            <button onClick={handleSubmit} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors">
              {isEditMode ? '수정 완료' : '계약 등록'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
